// Detail sub-components — BriefEditor (+ phase action panel/modals), CommentsThread,
// ActivityLog. Split from SampleRequestDetail to keep each file readable.

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Trash2, Lock, ChevronLeft, ChevronRight, Check, Send,
  CalendarDays, PackageCheck, Truck, ExternalLink, Pencil,
} from 'lucide-react'
import { Card } from '../../components/ui/card'
import {
  type Brief, type Sample, type SampleComment, type SampleActivity,
} from './lib'
import {
  changePhase, factoryConfirm, markReady, markShipped, markReceived, deleteSample,
  postComment, editComment, deleteComment, setPriority, setSampleMaterial, setSamplePrice, setTargetPrice, setFactoryNotes,
} from './api'
import { toast } from '../../lib/toast'
import { PrioritySelector } from './PrioritySelector'
import { useEscapeKey } from '../../lib/useEscapeKey'
import { useT } from '../../i18n'
import { sampleDetailPartsI18n } from '../../i18n/samples/detail-parts'
import { samplesCommonI18n } from '../../i18n/samples/common'
import { SpecsEditor } from './SpecsEditor'

function photoUrl(p: { url: string } | string): string {
  return typeof p === 'string' ? p : p.url
}
function todayIso() {
  return new Date().toISOString().slice(0, 10)
}
function defaultEtaIso() {
  const d = new Date()
  d.setDate(d.getDate() + 14)
  return d.toISOString().slice(0, 10)
}

export function BriefEditor({
  id, brief, setBrief, saving, locked, isFactory, sample, basePath, onPhaseChange,
}: {
  id: string
  brief: Brief | null
  setBrief: (b: Brief) => void
  saving: boolean
  locked: boolean
  isFactory: boolean
  sample: Sample
  basePath: string
  onPhaseChange: () => void
}) {
  const navigate = useNavigate()
  const t = useT(sampleDetailPartsI18n)
  const [photoUrlInput, setPhotoUrlInput] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [modal, setModal] = useState<null | 'handoff' | 'confirm' | 'eta' | 'unlock' | 'ready' | 'shipped' | 'received' | 'delete'>(null)
  useEscapeKey((modal === 'eta' || modal === 'shipped') && !busy, () => setModal(null))
  const [eta, setEta] = useState(defaultEtaIso())
  const [ship, setShip] = useState({ carrier: '', tracking_number: '', tracking_url: '', note: '' })

  if (!brief) return <div className="text-sm text-[var(--color-text-muted)]">{t.loadingBrief}</div>
  const phase = sample.status
  const photos = brief.photos || []
  const specs = brief.specs || []
  // The brief is the customer's to author. A factory views it read-only — it
  // only sets Material (and comments). `locked` (confirmed+) locks everyone.
  const fieldsLocked = locked || isFactory

  function movePhoto(from: number, to: number) {
    if (to < 0 || to >= photos.length) return
    const next = [...photos]
    const [m] = next.splice(from, 1)
    next.splice(to, 0, m)
    setBrief({ ...brief!, photos: next })
  }

  async function runPhase(fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      setModal(null)
      onPhaseChange()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.myBrief}</h2>
        <span className="text-xs">
          {saving ? (
            <span className="text-[var(--color-text-muted)]">{t.saving}</span>
          ) : locked ? (
            <span className="inline-flex items-center gap-1 text-amber-700"><Lock size={12} /> {t.locked}</span>
          ) : (
            <span className="text-[var(--color-text-muted)]">{t.autoSaved}</span>
          )}
        </span>
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-[var(--color-text)]">{t.productName} <span className="text-rose-500 appearance-dark:text-[var(--color-danger)]">*</span></label>
        <input
          type="text" value={brief.product_name} disabled={fieldsLocked}
          onChange={(e) => setBrief({ ...brief, product_name: e.target.value })}
          placeholder={t.productNamePlaceholder}
          className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-[var(--color-surface-muted)]"
        />
      </div>

      {!isFactory && (
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-[var(--color-text)]">{t.priority}</label>
          <PrioritySelector value={sample.priority} editable={!locked} size="md"
            onChange={async (v) => { await setPriority(id, v); onPhaseChange() }} />
        </div>
      )}

      {(!fieldsLocked || photos.length > 0) && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-[var(--color-text)]">{t.briefPhotos}</label>
          {!fieldsLocked && (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t.briefPhotosHintBefore}<span className="font-medium">{t.briefPhotosHintUse}</span>.
            </p>
          )}
          {!fieldsLocked && (
            <div className="flex items-center gap-2">
              <input value={photoUrlInput} onChange={(e) => setPhotoUrlInput(e.target.value)} type="url" placeholder={t.pasteImageUrl}
                className="flex-1 px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              <button type="button" disabled={!photoUrlInput}
                onClick={() => { setBrief({ ...brief, photos: [...photos, { url: photoUrlInput.trim() }] }); setPhotoUrlInput('') }}
                className="whitespace-nowrap text-xs px-3 py-2 border border-[var(--color-border-strong)] rounded hover:bg-[var(--color-surface-muted)] disabled:opacity-50">{t.addUrl}</button>
            </div>
          )}
          {!fieldsLocked && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false)
                const uri = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain')
                const url = uri.trim()
                if (url.startsWith('http')) setBrief({ ...brief, photos: [...photos, { url }] })
              }}
              className={`rounded-lg border-2 border-dashed px-3 py-3 text-center text-xs transition ${dragOver ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 text-[var(--color-text-muted)]'}`}
            >
              {dragOver ? t.dropToAdd : t.dragHint}
            </div>
          )}
          {photos.length > 0 ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((p, i) => {
                const u = photoUrl(p)
                return (
                  <div key={`${u}-${i}`} className="group relative aspect-square overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]">
                    <img src={u} className="h-full w-full object-cover" alt="" loading="lazy" />
                    {i === 0 && <span className="absolute left-1 top-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white">{t.main}</span>}
                    {!fieldsLocked && (
                      <>
                        <button title={t.remove} className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-[var(--color-surface)]/95 text-[var(--color-text-muted)] hover:text-rose-600"
                          onClick={() => setBrief({ ...brief, photos: photos.filter((_, idx) => idx !== i) })}><Trash2 size={13} /></button>
                        {photos.length > 1 && (
                          <div className="absolute inset-x-1 bottom-1 flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
                            <button disabled={i === 0} className="grid h-6 w-6 place-items-center rounded-full bg-[var(--color-surface)]/90 text-[var(--color-text)] disabled:opacity-40" onClick={() => movePhoto(i, i - 1)}><ChevronLeft size={10} /></button>
                            <button disabled={i === photos.length - 1} className="grid h-6 w-6 place-items-center rounded-full bg-[var(--color-surface)]/90 text-[var(--color-text)] disabled:opacity-40" onClick={() => movePhoto(i, i + 1)}><ChevronRight size={10} /></button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)]">{fieldsLocked ? t.noPhotos : t.noPhotosYet}</p>
          )}
        </div>
      )}

      {(!locked || specs.length > 0) && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-[var(--color-text)]">{t.specs}</label>
          <SpecsEditor
            specs={specs} disabled={locked} restrictToMaterial={isFactory}
            onChange={(next) => setBrief({ ...brief, specs: next })}
            onMaterialChange={isFactory ? async (v) => { await setSampleMaterial(id, v); onPhaseChange() } : undefined}
          />
        </div>
      )}

      {(!locked || brief.price || brief.target_price) && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <label className="block text-xs font-medium text-[var(--color-text)]">{t.price}</label>
              <span className="text-[10px] text-[var(--color-text-muted)]">{t.producer}</span>
            </div>
            <PriceField initial={brief.price ?? ''} editable={isFactory && !locked} onSave={(v) => { void setSamplePrice(id, v) }} />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <label className="block text-xs font-medium text-[var(--color-text)]">{t.targetPrice}</label>
              <span className="text-[10px] text-[var(--color-text-muted)]">{t.buyer}</span>
            </div>
            <PriceField initial={brief.target_price ?? ''} editable={!isFactory && !locked} onSave={(v) => { void setTargetPrice(id, v) }} />
          </div>
        </div>
      )}

      {(!fieldsLocked || brief.notes) && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-[var(--color-text)]">{t.productDetails}</label>
          <textarea value={brief.notes} disabled={fieldsLocked} rows={4} placeholder={fieldsLocked ? '' : t.productDetailsPlaceholder}
            onChange={(e) => setBrief({ ...brief, notes: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-[var(--color-surface-muted)]" />
        </div>
      )}

      {isFactory && (!locked || brief.factory_notes) && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="block text-xs font-medium text-[var(--color-text)]">{t.productionNotes}</label>
            <span className="text-[10px] text-[var(--color-text-muted)]">{t.privateToYou}</span>
          </div>
          <FactoryNotesField id={id} initial={brief.factory_notes ?? ''} editable={!locked} />
        </div>
      )}

      <div className="sticky bottom-0 -mx-4 -mb-4 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-muted)]/95 p-4 backdrop-blur">
        <PhaseBanner sample={sample} isFactory={isFactory} />
        <div className="flex flex-wrap gap-2">
          {phase === 'draft' && (
            <button className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setModal('handoff')}>
              <Send size={14} /> {t.saveOpenToFactory}
            </button>
          )}
          {phase === 'ready_for_factory' && (
            <>
              <button className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded inline-flex items-center gap-1" disabled={busy}
                onClick={() => { if (isFactory) { setEta(defaultEtaIso()); setModal('eta') } else setModal('confirm') }}>
                <Check size={14} /> {t.confirmToProduction}
              </button>
              {!isFactory && <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded hover:bg-[var(--color-surface-muted)]" disabled={busy} onClick={() => runPhase(() => changePhase(id, 'draft'))}>{t.backToEditing}</button>}
            </>
          )}
          {phase === 'confirmed' && (
            <>
              {isFactory && <button className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setModal('ready')}><PackageCheck size={14} /> {t.markSampleReady}</button>}
              {!isFactory && <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded hover:bg-[var(--color-surface-muted)] inline-flex items-center gap-1" disabled={busy} onClick={() => setModal('unlock')}><Lock size={14} /> {t.unlock}</button>}
            </>
          )}
          {phase === 'sample_ready' && isFactory && (
            <button className="text-xs px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => { setShip({ carrier: '', tracking_number: '', tracking_url: '', note: '' }); setModal('shipped') }}>
              <Truck size={14} /> {t.markAsShipped}
            </button>
          )}
          {phase === 'shipped' && !isFactory && (
            <button className="text-xs px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setModal('received')}>
              <PackageCheck size={14} /> {t.sampleReceived}
            </button>
          )}
          {phase !== 'shipped' && phase !== 'received' && !isFactory && (
            <button className="text-xs px-3 py-1.5 border border-teal-300 text-teal-700 rounded hover:bg-teal-50 inline-flex items-center gap-1" disabled={busy} onClick={() => setModal('received')}>
              <PackageCheck size={14} /> {t.sampleReceivedOverride}
            </button>
          )}
          {(phase === 'shipped' || phase === 'received') && sample.tracking_url && (
            <a href={sample.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded hover:bg-[var(--color-surface-muted)] inline-flex items-center gap-1"><ExternalLink size={14} /> {t.trackPackage}</a>
          )}
          {phase === 'draft' && !isFactory && (
            <button className="ml-auto text-xs px-3 py-1.5 text-rose-600 hover:bg-rose-50 rounded inline-flex items-center gap-1" disabled={busy} onClick={() => setModal('delete')}>
              <Trash2 size={14} /> {t.deleteSample}
            </button>
          )}
        </div>
      </div>

      {modal === 'handoff' && <ConfirmModal title={t.handoff.title} body={t.handoff.body} confirm={t.handoff.confirm} busy={busy} onCancel={() => setModal(null)} onConfirm={() => runPhase(() => changePhase(id, 'ready_for_factory'))} />}
      {modal === 'confirm' && <ConfirmModal title={t.confirm.title} body={t.confirm.body} confirm={t.confirm.confirm} warn busy={busy} onCancel={() => setModal(null)} onConfirm={() => runPhase(() => changePhase(id, 'confirmed'))} />}
      {modal === 'unlock' && <ConfirmModal title={t.unlockModal.title} body={t.unlockModal.body} confirm={t.unlockModal.confirm} busy={busy} onCancel={() => setModal(null)} onConfirm={() => runPhase(() => changePhase(id, 'draft'))} />}
      {modal === 'ready' && <ConfirmModal title={t.ready.title} body={t.ready.body} confirm={t.ready.confirm} busy={busy} onCancel={() => setModal(null)} onConfirm={() => runPhase(async () => {
        await markReady(id)
        if (isFactory && !(brief!.price || '').trim()) {
          toast.warning(t.priceMissing, { description: t.priceMissingBody })
        }
      })} />}
      {modal === 'received' && <ConfirmModal title={t.receivedTitle} body={phase === 'shipped'
        ? t.receivedBodyShipped
        : t.receivedBodyOverride}
        confirm={t.sampleReceived} warn={phase !== 'shipped'} busy={busy} onCancel={() => setModal(null)} onConfirm={() => runPhase(() => markReceived(id))} />}
      {modal === 'delete' && <ConfirmModal title={t.deleteModal.title} body={t.deleteModal.body} confirm={t.deleteModal.confirm} danger busy={busy} onCancel={() => setModal(null)} onConfirm={() => runPhase(async () => { await deleteSample(id); navigate({ to: basePath }) })} />}
      {modal === 'eta' && (
        <div role="presentation" className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <Card className="w-full max-w-sm p-5 space-y-3">
            <h3 className="text-base font-semibold text-[var(--color-text)]">{t.etaTitle}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t.etaBody}</p>
            <label className="block text-xs font-medium text-[var(--color-text)]">{t.etaLabel}</label>
            <input type="date" value={eta} min={todayIso()} onChange={(e) => setEta(e.target.value)} className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded" />
            <div className="flex justify-end gap-2">
              <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded" disabled={busy} onClick={() => setModal(null)}>{t.cancel}</button>
              <button className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded disabled:opacity-50" disabled={busy || !eta}
                onClick={() => runPhase(() => factoryConfirm(id, Math.floor(new Date(eta + 'T12:00:00').getTime() / 1000)))}>{t.confirmButton}</button>
            </div>
          </Card>
        </div>
      )}
      {modal === 'shipped' && (
        <div role="presentation" className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <Card className="w-full max-w-sm p-5 space-y-2.5">
            <h3 className="text-base font-semibold text-[var(--color-text)]">{t.markAsShipped}</h3>
            {(['carrier', 'tracking_number', 'tracking_url', 'note'] as const).map((f) => (
              <input key={f} value={ship[f]} onChange={(e) => setShip({ ...ship, [f]: e.target.value })}
                placeholder={t.shipPlaceholders[f]}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded" />
            ))}
            <div className="flex justify-end gap-2 pt-1">
              <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded" disabled={busy} onClick={() => setModal(null)}>{t.cancel}</button>
              <button className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded disabled:opacity-50" disabled={busy || (!ship.carrier.trim() && !ship.tracking_number.trim())}
                onClick={() => runPhase(() => markShipped(id, ship))}>{t.markShipped}</button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

// Price is USD-canonical (V1): the user types a dollar amount, it's stored and
// shown as "12.50 USD".
function parseAmount(s: string): string {
  const m = (s || '').match(/[\d.]+/)
  return m ? m[0] : ''
}
function formatUsd(rawAmount: string): string {
  const n = parseFloat(rawAmount)
  return Number.isFinite(n) ? `${n.toFixed(2)} USD` : ''
}
function cleanNumeric(v: string): string {
  const stripped = v.replace(/[^\d.]/g, '')
  const parts = stripped.split('.')
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : stripped
}

function PriceField({ initial, editable, onSave }: { initial: string; editable: boolean; onSave: (formatted: string) => void }) {
  const t = useT(sampleDetailPartsI18n)
  const [amount, setAmount] = useState(parseAmount(initial))
  const focused = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSave = useRef<(() => void) | null>(null)

  // Sync from the server value when it changes externally — never mid-edit.
  useEffect(() => { if (!focused.current) setAmount(parseAmount(initial)) }, [initial])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
    if (pendingSave.current) pendingSave.current()
  }, [])

  function save(rawAmount: string) {
    pendingSave.current = null
    onSave(formatUsd(rawAmount))
  }
  function change(v: string) {
    const cleaned = cleanNumeric(v)
    setAmount(cleaned)
    if (timer.current) clearTimeout(timer.current)
    pendingSave.current = () => save(cleaned)
    timer.current = setTimeout(() => save(cleaned), 600)
  }
  function blur() {
    focused.current = false
    const n = parseFloat(amount)
    const norm = Number.isFinite(n) ? n.toFixed(2) : ''
    setAmount(norm)
    if (timer.current) clearTimeout(timer.current)
    save(norm)
  }

  if (!editable) {
    const display = formatUsd(parseAmount(initial))
    return (
      <div className="text-sm text-[var(--color-text)]">
        {display || <span className="text-[var(--color-text-muted)]">{t.noPriceYet}</span>}
      </div>
    )
  }
  return (
    <div className="relative max-w-[12rem]">
      <input
        type="text"
        inputMode="decimal"
        value={amount}
        onFocus={() => { focused.current = true }}
        onBlur={blur}
        onChange={(e) => change(e.target.value)}
        placeholder="0.00"
        className="w-full px-3 py-2 pr-12 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[var(--color-text-muted)]">USD</span>
    </div>
  )
}

function FactoryNotesField({ id, initial, editable }: { id: string; initial: string; editable: boolean }) {
  const t = useT(sampleDetailPartsI18n)
  const [val, setVal] = useState(initial)
  const focused = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSave = useRef<(() => void) | null>(null)

  useEffect(() => { if (!focused.current) setVal(initial) }, [initial])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
    if (pendingSave.current) pendingSave.current()
  }, [])

  function save(v: string) {
    pendingSave.current = null
    void setFactoryNotes(id, v)
  }
  function change(v: string) {
    setVal(v)
    if (timer.current) clearTimeout(timer.current)
    pendingSave.current = () => save(v)
    timer.current = setTimeout(() => save(v), 600)
  }

  if (!editable) {
    return val ? (
      <div className="whitespace-pre-wrap rounded border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-text)]">{val}</div>
    ) : (
      <div className="text-sm text-[var(--color-text-muted)]">{t.noProductionNotes}</div>
    )
  }
  return (
    <textarea
      value={val}
      rows={4}
      onFocus={() => { focused.current = true }}
      onBlur={() => { focused.current = false; if (timer.current) clearTimeout(timer.current); save(val) }}
      onChange={(e) => change(e.target.value)}
      placeholder={t.productionNotesPlaceholder}
      className="w-full px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
  )
}

function PhaseBanner({ sample, isFactory }: { sample: Sample; isFactory: boolean }) {
  const t = useT(sampleDetailPartsI18n)
  const c = useT(samplesCommonI18n)
  const p = sample.status
  if (p === 'draft') return <div><div className="text-sm font-semibold text-[var(--color-text)]">{t.bannerDraftTitle}</div><div className="text-xs text-[var(--color-text-muted)]">{t.bannerDraftBody}</div></div>
  if (p === 'ready_for_factory') return <div className="text-sm font-semibold text-blue-800">{t.bannerReadyForFactory}{!isFactory && <span className="ml-1 font-normal text-[var(--color-text-muted)]">{t.bannerReadOnly}</span>}</div>
  if (p === 'confirmed') return (
    <div>
      <div className="flex items-center gap-1.5 text-sm font-semibold text-[var(--color-success)]"><Check size={14} /> {t.bannerInProduction}</div>
      {sample.expected_completion_at && (
        <div className="mt-2 rounded-lg border border-emerald-200 bg-[var(--color-surface)]/70 p-3 text-xs">
          <div className="flex items-center gap-2"><CalendarDays size={14} className="text-[var(--color-success)]" /><span className="font-semibold text-[var(--color-text)]">{t.expectedCompletion}</span><span className="tabular-nums text-[var(--color-text)]">{c.fmtDate(sample.expected_completion_at)}</span></div>
        </div>
      )}
    </div>
  )
  if (p === 'sample_ready') return <div className="flex items-center gap-1.5 text-sm font-semibold text-violet-800"><PackageCheck size={14} /> {t.bannerSampleReady}</div>
  if (p === 'shipped') return (
    <div>
      <div className="flex items-center gap-1.5 text-sm font-semibold text-cyan-800"><Truck size={14} /> {t.bannerShipped}</div>
      <div className="mt-1 space-y-0.5 text-xs text-[var(--color-text-muted)]">
        {sample.shipping_carrier && <div>{t.carrier}<span className="text-[var(--color-text)]">{sample.shipping_carrier}</span></div>}
        {sample.tracking_number && <div>{t.tracking}<span className="font-mono text-[var(--color-text)]">{sample.tracking_number}</span></div>}
      </div>
    </div>
  )
  if (p === 'received') return (
    <div>
      <div className="flex items-center gap-1.5 text-sm font-semibold text-teal-800"><PackageCheck size={14} /> {t.bannerReceived}{sample.received_at && <span className="font-normal text-[var(--color-text-muted)]">— {c.fmtDate(sample.received_at)}</span>}</div>
      {isFactory && <div className="mt-1 text-xs text-[var(--color-text-muted)]">{t.customerConfirmed}</div>}
    </div>
  )
  return null
}

function ConfirmModal({ title, body, confirm, danger, warn, busy, onCancel, onConfirm }: {
  title: string; body: string; confirm: string; danger?: boolean; warn?: boolean; busy: boolean; onCancel: () => void; onConfirm: () => void
}) {
  const t = useT(sampleDetailPartsI18n)
  useEscapeKey(!busy, onCancel)
  const cls = danger ? 'bg-rose-600' : warn ? 'bg-amber-500' : 'bg-indigo-600'
  return (
    <div role="presentation" className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <Card className="w-full max-w-sm p-5 space-y-3">
        <h3 className="text-base font-semibold text-[var(--color-text)]">{title}</h3>
        <p className="text-sm text-[var(--color-text-muted)]">{body}</p>
        <div className="flex justify-end gap-2">
          <button className="text-xs px-3 py-1.5 border border-[var(--color-border-strong)] rounded" disabled={busy} onClick={onCancel}>{t.cancel}</button>
          <button className={`text-xs px-3 py-1.5 text-white rounded disabled:opacity-50 ${cls}`} disabled={busy} onClick={onConfirm}>{busy ? '…' : confirm}</button>
        </div>
      </Card>
    </div>
  )
}

export function CommentsThread({ id, comments, isFactory, onChange }: { id: string; comments: SampleComment[]; isFactory: boolean; onChange: () => void }) {
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const myRole = isFactory ? 'factory' : 'user'
  const t = useT(sampleDetailPartsI18n)
  const sc = useT(samplesCommonI18n)

  return (
    <Card className="p-4">
      <h2 className="mb-3 text-lg font-semibold text-[var(--color-text)]">{t.comments} <span className="text-sm font-medium text-[var(--color-text-muted)]">({comments.length})</span></h2>
      <div className="space-y-3">
        {comments.map((c) => (
          <div key={c.id} className="flex gap-2.5">
            <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${c.role === 'factory' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
              {(c.actor || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[var(--color-text)]">{c.actor}</span>
                <span className="text-[11px] text-[var(--color-text-muted)]">{sc.relTime(c.created_at)}{c.edited ? t.edited : ''}</span>
              </div>
              {editId === c.id ? (
                <div className="mt-1 flex items-center gap-2">
                  <input value={editBody} onChange={(e) => setEditBody(e.target.value)} className="flex-1 px-2 py-1 text-sm border border-[var(--color-border-strong)] rounded" />
                  <button className="text-xs text-indigo-600" onClick={async () => { await editComment(id, c.id, editBody); setEditId(null); onChange() }}>{t.save}</button>
                  <button className="text-xs text-[var(--color-text-muted)]" onClick={() => setEditId(null)}>{t.cancel}</button>
                </div>
              ) : (
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-[var(--color-text)]">{c.body}</p>
              )}
            </div>
            {c.role === myRole && editId !== c.id && (
              <div className="flex shrink-0 items-start gap-0.5">
                <button title={t.edit} className="grid h-7 w-7 place-items-center rounded text-[var(--color-text-muted)] hover:text-indigo-600" onClick={() => { setEditId(c.id); setEditBody(c.body) }}><Pencil size={12} /></button>
                <button title={t.delete} className="grid h-7 w-7 place-items-center rounded text-[var(--color-text-muted)] hover:text-rose-600" onClick={async () => { await deleteComment(id, c.id); onChange() }}><Trash2 size={12} /></button>
              </div>
            )}
          </div>
        ))}
        {comments.length === 0 && <p className="text-sm text-[var(--color-text-muted)]">{t.noComments}</p>}
      </div>
      <form className="mt-4 flex items-center gap-2" onSubmit={async (e) => { e.preventDefault(); if (!body.trim()) return; setBusy(true); try { await postComment(id, body.trim()); setBody(''); onChange() } finally { setBusy(false) } }}>
        <input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t.writeComment} className="flex-1 px-3 py-2 text-sm border border-[var(--color-border-strong)] rounded focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        <button type="submit" disabled={busy || !body.trim()} className="text-xs px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-50">{t.send}</button>
      </form>
    </Card>
  )
}

export function ActivityLog({ activity }: { activity: SampleActivity[] }) {
  const t = useT(sampleDetailPartsI18n)
  const c = useT(samplesCommonI18n)
  function describe(a: SampleActivity): string {
    if (a.kind === 'phase_changed') return t.activityMovedTo(c.phaseLabel((a.detail?.to as string) || ''), !!a.detail?.override)
    if (a.kind === 'priority_changed') return t.activityChangedPriority
    return a.kind.replace(/_/g, ' ')
  }
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-[var(--color-border)] px-4 py-2.5"><h2 className="text-sm font-semibold text-[var(--color-text)]">{t.activity}</h2></div>
      <div className="p-4">
        {activity.length === 0 ? (
          <p className="text-xs text-[var(--color-text-muted)]">{t.noActivity}</p>
        ) : (
          <ol className="space-y-2">
            {activity.map((a) => (
              <li key={a.id} className="flex items-baseline gap-2 text-xs">
                <span className="font-medium text-[var(--color-text)]">{a.actor || t.someone}</span>
                <span className="text-[var(--color-text-muted)]">{describe(a)}</span>
                <span className="ml-auto text-[var(--color-text-muted)]">{c.relTime(a.created_at)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Card>
  )
}
